import React, { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { aggregatorsApi, AggregatorDetail, AggregatorEarning, Page } from '../../api/aggregators';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { Drawer } from '../../components/ui/Drawer';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Spinner } from '../../components/ui/Spinner';
import { StatCard } from '../../components/ui/StatCard';
import { date, eventText, naira, sectionNote, sectionTitle, SOURCE_LABELS, StatusBadge, td, th } from './shared';

type Action = 'pause' | 'resume' | 'suspend' | 'reinstate' | 'revoke';

const ACTION_COPY: Record<Action, { title: string; message: string; label: string; reason: boolean; danger: boolean }> =
  {
    pause: {
      title: 'Pause withdrawals',
      message:
        'They keep earning and can see their balance, but cannot withdraw until you resume. The app only tells them withdrawals are paused; your reason stays here.',
      label: 'Pause withdrawals',
      reason: true,
      danger: true,
    },
    resume: {
      title: 'Resume withdrawals',
      message: 'They will be able to withdraw their earnings again, and are told so.',
      label: 'Resume',
      reason: false,
      danger: false,
    },
    suspend: {
      title: 'Suspend this aggregator',
      message:
        'Their businesses stop earning them anything until you reinstate them. Revenue booked while they are suspended is never earned back. Withdrawals are a separate switch.',
      label: 'Suspend',
      reason: true,
      danger: true,
    },
    reinstate: {
      title: 'Reinstate this aggregator',
      message: 'Their businesses earn for them again, on revenue from now on.',
      label: 'Reinstate',
      reason: false,
      danger: false,
    },
    revoke: {
      title: 'Revoke for good',
      message:
        'They stop earning permanently and cannot apply again. What they have already earned is still theirs to withdraw unless you also pause withdrawals.',
      label: 'Revoke',
      reason: true,
      danger: true,
    },
  };

export const AggregatorDrawer: React.FC<{ id: string | null; onClose: () => void; onChanged: () => void }> = ({
  id,
  onClose,
  onChanged,
}) => {
  const toast = useToast();
  const { can } = useAuth();
  const [detail, setDetail] = useState<AggregatorDetail | null>(null);
  const [earnings, setEarnings] = useState<Page<AggregatorEarning> | null>(null);
  const [action, setAction] = useState<Action | null>(null);
  const [rateOpen, setRateOpen] = useState(false);
  const [rate, setRate] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const [d, e] = await Promise.all([aggregatorsApi.detail(id), aggregatorsApi.earnings(id)]);
      setDetail(d);
      setEarnings(e);
    } catch (err) {
      toast.error(err, 'Could not load this aggregator.');
      onClose();
    }
  }, [id, toast, onClose]);

  useEffect(() => {
    setDetail(null);
    setEarnings(null);
    void load();
  }, [load]);

  async function run(fn: () => Promise<unknown>, success: string) {
    setBusy(true);
    try {
      await fn();
      toast.success(success);
      await load();
      onChanged();
      return true;
    } catch (err) {
      toast.error(err, 'That did not work.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function confirmAction(reason?: string) {
    if (!id || !action) return;
    const r = reason?.trim() ?? '';
    const calls: Record<Action, () => Promise<unknown>> = {
      pause: () => aggregatorsApi.pauseWithdrawals(id, r),
      resume: () => aggregatorsApi.resumeWithdrawals(id),
      suspend: () => aggregatorsApi.suspend(id, r),
      reinstate: () => aggregatorsApi.reinstate(id),
      revoke: () => aggregatorsApi.revoke(id, r),
    };
    if (await run(calls[action], 'Done.')) setAction(null);
  }

  const rateValue = Number(rate);
  const rateValid = rate.trim() !== '' && rateValue > 0 && rateValue <= 50;

  const canManage = can('aggregators.manage');
  const a = detail?.aggregator;
  const copy = action ? ACTION_COPY[action] : null;

  return (
    <Drawer
      isOpen={!!id}
      onClose={onClose}
      title={detail?.person?.name ?? 'Aggregator'}
      subtitle={detail?.person?.email ?? detail?.person?.phone ?? undefined}
      width={760}
    >
      {!detail || !a ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spinner />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <StatusBadge status={a.status} />
            {a.withdrawalsPaused ? <Badge variant="warning">Withdrawals paused</Badge> : null}
            <Badge variant="info">{a.commissionPercent}% share</Badge>
            <span style={{ fontSize: 12, color: 'var(--neutral-500)' }}>Approved {date(a.approvedAt)}</span>
          </div>
          {a.statusReason || a.withdrawalsPausedReason ? (
            <p style={{ ...sectionNote, margin: 0 }}>
              {a.statusReason ? `Why ${a.status}: ${a.statusReason}. ` : ''}
              {a.withdrawalsPausedReason ? `Why withdrawals are paused: ${a.withdrawalsPausedReason}.` : ''}
            </p>
          ) : null}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
            <StatCard label="Ready to withdraw" value={naira(detail.balance.available)} accent="green" />
            <StatCard label="Earned in total" value={naira(detail.balance.earned)} accent="blue" />
            <StatCard label="This month" value={naira(detail.balance.thisMonth)} accent="neutral" />
            <StatCard label="Withdrawn" value={naira(detail.balance.paidOut)} accent="neutral" />
          </div>

          {canManage ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {a.status !== 'revoked' ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setRate(String(a.commissionPercent));
                    setNote('');
                    setRateOpen(true);
                  }}
                >
                  Change share
                </Button>
              ) : null}
              <Button variant="secondary" onClick={() => setAction(a.withdrawalsPaused ? 'resume' : 'pause')}>
                {a.withdrawalsPaused ? 'Resume withdrawals' : 'Pause withdrawals'}
              </Button>
              {a.status === 'active' ? (
                <Button variant="ghost" onClick={() => setAction('suspend')}>
                  Suspend
                </Button>
              ) : null}
              {a.status === 'suspended' ? (
                <Button variant="ghost" onClick={() => setAction('reinstate')}>
                  Reinstate
                </Button>
              ) : null}
              {a.status !== 'revoked' ? (
                <Button variant="danger" onClick={() => setAction('revoke')}>
                  Revoke
                </Button>
              ) : null}
            </div>
          ) : null}

          {detail.reviews.length ? (
            <section>
              <h3 style={sectionTitle}>Businesses that look like their own</h3>
              <p style={sectionNote}>
                Their phone or email matches the business, someone on its team, or a director. Flagged ones still
                earn until you decide; blocked ones earn nothing from then on.
              </p>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <tbody>
                  {detail.reviews.map((r) => (
                    <tr key={r.id} style={{ borderTop: '1px solid var(--neutral-300)' }}>
                      <td style={td}>
                        <div style={{ fontWeight: 600 }}>{r.businessName}</div>
                        <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>{r.reason}</div>
                      </td>
                      <td style={td}>
                        <Badge variant={r.status === 'blocked' ? 'danger' : r.status === 'flagged' ? 'warning' : 'success'}>
                          {r.status === 'blocked' ? 'Blocked' : r.status === 'flagged' ? 'Flagged' : 'Cleared'}
                        </Badge>
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                        {canManage && r.status !== 'cleared' ? (
                          <Button
                            variant="ghost"
                            disabled={busy}
                            onClick={() =>
                              void run(() => aggregatorsApi.reviewBusiness(r.id, 'cleared'), 'Cleared. It earns as normal.')
                            }
                          >
                            Clear
                          </Button>
                        ) : null}
                        {canManage && r.status !== 'blocked' ? (
                          <Button
                            variant="ghost"
                            disabled={busy}
                            onClick={() =>
                              void run(() => aggregatorsApi.reviewBusiness(r.id, 'blocked'), 'Blocked. It earns nothing from now.')
                            }
                          >
                            Block
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ) : null}

          <section>
            <h3 style={sectionTitle}>Businesses ({detail.businesses.meta.total})</h3>
            {detail.businesses.items.length === 0 ? (
              <p style={sectionNote}>None yet.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', minWidth: 620, borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--neutral-200)' }}>
                      {['Business', 'Joined', 'This month', 'Last month', 'Earned them', ''].map((h) => (
                        <th key={h} style={th}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {detail.businesses.items.map((b) => (
                      <tr key={b.id} style={{ borderTop: '1px solid var(--neutral-300)' }}>
                        <td style={{ ...td, fontWeight: 600 }}>{b.name}</td>
                        <td style={td}>{date(b.joinedAt)}</td>
                        <td style={td}>{naira(b.volumeThisMonth)}</td>
                        <td style={td}>{naira(b.volumeLastMonth)}</td>
                        <td style={td}>{naira(b.earned)}</td>
                        <td style={td}>{b.earning ? null : <Badge variant="neutral">Not earning</Badge>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section>
            <h3 style={sectionTitle}>Recent earnings</h3>
            {!earnings?.items.length ? (
              <p style={sectionNote}>None yet.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--neutral-200)' }}>
                    {['When', 'Business', 'From', 'PayIQ earned', 'Share', 'Them'].map((h) => (
                      <th key={h} style={th}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {earnings.items.map((e) => (
                    <tr key={e.id} style={{ borderTop: '1px solid var(--neutral-300)' }}>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{date(e.occurredAt)}</td>
                      <td style={td}>{e.businessName}</td>
                      <td style={td}>{e.correction ? 'Reversal' : SOURCE_LABELS[e.source] ?? e.source}</td>
                      <td style={td}>{naira(e.revenueAmount)}</td>
                      <td style={td}>{e.commissionPercent}%</td>
                      <td style={{ ...td, fontWeight: 600 }}>{naira(e.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section>
            <h3 style={sectionTitle}>Withdrawals</h3>
            {!detail.payouts.length ? (
              <p style={sectionNote}>None yet.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--neutral-200)' }}>
                    {['When', 'From earnings', 'Withholding tax', 'Paid', 'Status'].map((h) => (
                      <th key={h} style={th}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {detail.payouts.map((p) => (
                    <tr key={p.id} style={{ borderTop: '1px solid var(--neutral-300)' }}>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{date(p.createdAt)}</td>
                      <td style={td}>{naira(p.gross)}</td>
                      <td style={td}>{naira(p.withholdingTax)}</td>
                      <td style={{ ...td, fontWeight: 600 }}>{naira(p.net)}</td>
                      <td style={td}>
                        <Badge variant={p.status === 'paid' ? 'success' : p.status === 'pending' ? 'info' : 'danger'}>
                          {p.status === 'paid' ? 'Paid' : p.status === 'pending' ? 'Waiting on the bank' : 'Failed'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {detail.application ? (
            <section>
              <h3 style={sectionTitle}>Their application</h3>
              <p style={{ ...sectionNote, whiteSpace: 'pre-wrap' }}>
                {detail.application.about}
                {'\n'}
                {detail.application.location} · about {detail.application.businessesPerMonth} a month
              </p>
            </section>
          ) : null}

          <section>
            <h3 style={sectionTitle}>History</h3>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--neutral-700)' }}>
              {detail.history.map((h) => (
                <li key={h.id} style={{ marginBottom: 6 }}>
                  <span style={{ color: 'var(--neutral-500)' }}>{date(h.createdAt)}</span> {eventText(h.kind, h.detail)}
                </li>
              ))}
            </ul>
          </section>
        </div>
      )}

      <Modal isOpen={rateOpen} onClose={() => setRateOpen(false)} title="Change their share" maxWidth={460}>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--neutral-600)' }}>
          Applies to revenue from now on. What they have already earned keeps the rate it was earned at. They are told
          the new rate.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Input
            label="Share (%)"
            type="number"
            step="0.01"
            min="0.01"
            max="50"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            error={rate.trim() === '' || rateValid ? undefined : 'Above 0% and at most 50%.'}
          />
          <Input label="Note for the history (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <Button variant="ghost" onClick={() => setRateOpen(false)}>
            Cancel
          </Button>
          <Button
            disabled={busy || !rateValid}
            onClick={() =>
              void (async () => {
                if (id && (await run(() => aggregatorsApi.setRate(id, rateValue, note.trim() || undefined), 'Share changed.'))) {
                  setRateOpen(false);
                }
              })()
            }
          >
            Save
          </Button>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!copy}
        title={copy?.title ?? ''}
        message={copy?.message ?? ''}
        confirmLabel={copy?.label}
        variant={copy?.danger ? 'danger' : 'primary'}
        requireReason={copy?.reason}
        reasonLabel="Reason (kept in the admin, never shown to them)"
        loading={busy}
        onConfirm={(reason) => void confirmAction(reason)}
        onCancel={() => setAction(null)}
      />
    </Drawer>
  );
};
